import { useMemo, type JSX } from 'react';
import ReactDiffViewer from 'react-diff-viewer-continued';
import { MonacoDiffEditor } from './MonacoDiffEditor';

/**
 * K1.2 (round-5) + W1.1 (v1.1) — dark-theme diff viewer.
 *
 * v1.0 shipped the pure-CSS `react-diff-viewer-continued` fallback.
 * W1.1 adds a syntax-aware Monaco path that kicks in when the
 * `monaco` prop is true OR when the filename hint maps to a language
 * Monaco handles well. The `MonacoDiffEditor` wrapper lazy-imports
 * `@monaco-editor/react` — absence falls back to the CSS viewer.
 */
export function DiffViewer({
  oldText,
  newText,
  oldTitle = 'Previous',
  newTitle = 'Proposed',
  splitView = true,
  /** W1.1 — opt-in Monaco. Defaults to true when a language hint is present. */
  monaco,
  /** Path or filename hint. Used both for Monaco language detection and UI titles. */
  filename,
}: {
  oldText: string;
  newText: string;
  oldTitle?: string;
  newTitle?: string;
  splitView?: boolean;
  monaco?: boolean;
  filename?: string;
}): JSX.Element {
  const language = useMemo(() => (filename ? languageFromFilename(filename) : undefined), [
    filename,
  ]);
  const useMonaco = monaco ?? !!language;
  if (useMonaco) {
    return (
      <div className="overflow-hidden rounded-sm border border-border-subtle">
        <div className="flex items-center justify-between border-b border-border-subtle bg-bg-raised px-3 py-1 text-2xs text-fg-muted">
          <span className="shakti-mono">{oldTitle}</span>
          <span className="shakti-mono">{newTitle}</span>
        </div>
        <MonacoDiffEditor
          original={oldText}
          modified={newText}
          language={language}
          height={480}
        />
      </div>
    );
  }
  return <CssFallbackDiffViewer oldText={oldText} newText={newText} oldTitle={oldTitle} newTitle={newTitle} splitView={splitView} />;
}

function languageFromFilename(filename: string): string | undefined {
  const name = filename.toLowerCase();
  const ext = name.includes('.') ? name.split('.').pop() : undefined;
  const byExt: Record<string, string> = {
    rs: 'rust',
    ts: 'typescript',
    tsx: 'typescript',
    js: 'javascript',
    jsx: 'javascript',
    py: 'python',
    go: 'go',
    java: 'java',
    kt: 'kotlin',
    swift: 'swift',
    c: 'c',
    cpp: 'cpp',
    cc: 'cpp',
    h: 'c',
    hpp: 'cpp',
    cs: 'csharp',
    rb: 'ruby',
    php: 'php',
    sh: 'shell',
    bash: 'shell',
    yaml: 'yaml',
    yml: 'yaml',
    json: 'json',
    toml: 'toml',
    md: 'markdown',
    sql: 'sql',
    html: 'html',
    css: 'css',
    scss: 'scss',
    proto: 'protobuf',
    graphql: 'graphql',
    gql: 'graphql',
    tf: 'hcl',
  };
  if (ext && byExt[ext]) return byExt[ext];
  if (name.endsWith('dockerfile') || name === 'dockerfile') return 'dockerfile';
  if (name.endsWith('.template.json')) return 'json';
  return undefined;
}

function CssFallbackDiffViewer({
  oldText,
  newText,
  oldTitle = 'Previous',
  newTitle = 'Proposed',
  splitView = true,
}: {
  oldText: string;
  newText: string;
  oldTitle?: string;
  newTitle?: string;
  splitView?: boolean;
}): JSX.Element {
  // Memoise the style object so the underlying <ReactDiffViewer> does
  // not re-generate its emotion classes on every render.
  const styles = useMemo(() => ({
    variables: {
      dark: {
        diffViewerBackground: '#0B0D10',
        diffViewerColor: '#E6EAF2',
        addedBackground: '#1e3a2d',
        addedColor: '#c8f5d7',
        removedBackground: '#3a1e24',
        removedColor: '#f5c8cf',
        wordAddedBackground: '#2f7a54',
        wordRemovedBackground: '#913a46',
        addedGutterBackground: '#164a33',
        removedGutterBackground: '#4a1820',
        gutterBackground: '#11141A',
        gutterBackgroundDark: '#0B0D10',
        highlightBackground: '#1C2230',
        highlightGutterBackground: '#1C2230',
        codeFoldGutterBackground: '#11141A',
        codeFoldBackground: '#11141A',
        emptyLineBackground: '#0B0D10',
        gutterColor: '#6F7989',
        addedGutterColor: '#c8f5d7',
        removedGutterColor: '#f5c8cf',
        codeFoldContentColor: '#6F7989',
        diffViewerTitleBackground: '#11141A',
        diffViewerTitleColor: '#E6EAF2',
        diffViewerTitleBorderColor: '#1F2632',
      },
    },
    contentText: {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '12px',
      lineHeight: '1.5',
    },
  }), []);

  return (
    <div className="overflow-x-auto rounded-sm border border-border-subtle">
      <ReactDiffViewer
        oldValue={oldText}
        newValue={newText}
        leftTitle={oldTitle}
        rightTitle={newTitle}
        splitView={splitView}
        useDarkTheme
        hideLineNumbers={false}
        extraLinesSurroundingDiff={2}
        styles={styles}
      />
    </div>
  );
}
